import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Sparkles, FileText, Heart, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

interface AIAssistPanelProps {
  conversationId: number | null;
  onUseSuggestion?: (text: string) => void;
}

// Formata "há X" para o timestamp do cache de IA. Usado pra dar contexto
// ao operador de quão antigo é o resultado, sem invalidar nada automaticamente.
function formatRelativeTime(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "agora mesmo";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} d`;
  return d.toLocaleDateString("pt-BR");
}

export default function AIAssistPanel({ conversationId, onUseSuggestion }: AIAssistPanelProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const prevConvIdRef = useRef<number | null>(null);

  const utils = trpc.useUtils();

  // Cache persistido no banco — hidrata o painel ao abrir a conversa.
  // Não dispara LLM; só lê o que já foi gerado anteriormente.
  const cacheQuery = trpc.inbox.aiCache.useQuery(
    { conversationId: conversationId ?? 0 },
    { enabled: conversationId != null }
  );

  const summary = cacheQuery.data?.aiSummary ?? null;
  const summaryAt = cacheQuery.data?.aiSummaryAt ?? null;
  const sentiment = cacheQuery.data?.aiSentiment ?? null;
  const sentimentEmoji = cacheQuery.data?.aiSentimentEmoji ?? null;
  const sentimentAt = cacheQuery.data?.aiSentimentAt ?? null;

  const suggestMut = trpc.inbox.aiSuggestReply.useMutation({
    onSuccess: (data) => setSuggestion(data.suggestion),
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro na sugestão")),
  });

  const summarizeMut = trpc.inbox.aiSummarize.useMutation({
    onSuccess: () => {
      if (conversationId != null) utils.inbox.aiCache.invalidate({ conversationId });
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro no resumo")),
  });

  const sentimentMut = trpc.inbox.aiSentiment.useMutation({
    onSuccess: () => {
      if (conversationId != null) utils.inbox.aiCache.invalidate({ conversationId });
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro no sentimento")),
  });

  // Limpar sugestão local ao trocar de conversa (sentimento/resumo vêm do cache do banco).
  useEffect(() => {
    if (conversationId !== prevConvIdRef.current) {
      prevConvIdRef.current = conversationId;
      setSuggestion(null);
    }
  }, [conversationId]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!conversationId) return null;

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      {/* Sentiment badge (visível quando há cache) */}
      {sentiment && sentimentEmoji && (
        <div className={`mx-3 mt-3 rounded-lg px-3 py-2 flex items-center gap-2 border text-xs ${
          sentiment === "positivo" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
          sentiment === "negativo" ? "bg-red-50 border-red-200 text-red-700" :
          "bg-gray-50 border-gray-200 text-gray-700"
        }`}>
          <span className="text-base">{sentimentEmoji}</span>
          <span className="font-medium capitalize">{sentiment}</span>
          <span className="text-muted-foreground ml-auto">
            {formatRelativeTime(sentimentAt) ?? "sentimento"}
          </span>
        </div>
      )}

      <div className="p-3 space-y-3">
        {/* Action cards */}
        <div className="space-y-2">
          <button
            className="w-full p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
            onClick={() => suggestMut.mutate({ conversationId })}
            disabled={suggestMut.isPending}
          >
            <div className="flex items-center gap-2 mb-0.5">
              {suggestMut.isPending ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
              <span className="text-sm font-medium">Sugerir resposta</span>
            </div>
            <p className="text-xs text-muted-foreground ml-6">IA analisa o contexto e sugere uma resposta</p>
          </button>

          <button
            className="w-full p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
            onClick={() => summarizeMut.mutate({ conversationId })}
            disabled={summarizeMut.isPending}
          >
            <div className="flex items-center gap-2 mb-0.5">
              {summarizeMut.isPending ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <FileText className="w-4 h-4 text-blue-500" />}
              <span className="text-sm font-medium">{summary ? "Atualizar resumo" : "Resumir conversa"}</span>
            </div>
            <p className="text-xs text-muted-foreground ml-6">Resume os pontos principais da conversa</p>
          </button>

          <button
            className="w-full p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
            onClick={() => sentimentMut.mutate({ conversationId })}
            disabled={sentimentMut.isPending}
          >
            <div className="flex items-center gap-2 mb-0.5">
              {sentimentMut.isPending ? <Loader2 className="w-4 h-4 animate-spin text-rose-500" /> : <Heart className="w-4 h-4 text-rose-500" />}
              <span className="text-sm font-medium">{sentiment ? "Reanalisar sentimento" : "Analisar sentimento"}</span>
            </div>
            <p className="text-xs text-muted-foreground ml-6">Identifica o sentimento do cliente</p>
          </button>
        </div>

        {/* Suggestion result */}
        {suggestion && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">Sugestão de resposta</span>
              <button
                onClick={() => handleCopy(suggestion)}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                title="Copiar"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
            <div className="text-xs bg-indigo-50 border border-indigo-100 rounded-lg p-2.5 whitespace-pre-wrap leading-relaxed">
              {suggestion}
            </div>
            {onUseSuggestion && (
              <Button
                size="sm"
                variant="default"
                className="w-full text-xs h-8 gap-1.5"
                onClick={() => onUseSuggestion(suggestion)}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Usar esta resposta
              </Button>
            )}
          </div>
        )}

        {/* Summary result */}
        {summary && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">Resumo</span>
              {summaryAt && (
                <span className="text-[10px] text-muted-foreground">{formatRelativeTime(summaryAt)}</span>
              )}
            </div>
            <div className="text-xs bg-blue-50 border border-blue-100 rounded-lg p-2.5 whitespace-pre-wrap leading-relaxed">
              {summary}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
